<script>
import WebLayout from '@/layouts/Web/Layout.vue'

export default {
    // Using shorthand syntax...
    layout: WebLayout,
}
</script>
<script setup>
import SEOHead from '@/components/SEOHead.vue'
</script>

<template>
    <SEOHead
        title="Politika zasebnosti - Kulfood"
        description="Politika zasebnosti Kulfood. Spoznajte, katere osebne podatke zbiramo, kako jih obdelujemo in katere pravice imate po GDPR."
        keywords="zasebnost, politika zasebnosti, Kulfood.si"
        canonical="/politika-zasebnosti"
        :structured-data="{
            '@type': 'WebSite',
            'name': 'Kulfood.si',
            'url': 'https://kulfood.si',
            'description': 'Politika zasebnosti Kulfood. Spoznajte, katere osebne podatke zbiramo, kako jih obdelujemo in katere pravice imate po GDPR.',
        }"
        type="website"
    />
    <div class="container mx-auto px-4 py-12">
        <div class="max-w-4xl mx-auto">
            <h1 class="text-4xl font-bold text-gray-900 mb-8 text-center">Politika zasebnosti</h1>
            
            <div class="prose prose-lg max-w-none">
                <div class="bg-linen border-l-4 border-theme-green-dark p-6 mb-8 rounded-r-lg">
                    <h2 class="text-2xl font-semibold text-gray-900 mb-4">Politika zasebnosti – Kulfood.si</h2>
                    
                    <div class="space-y-2 text-gray-700">
                        <p>
                            <strong class="text-gray-900">KulFood – Rok Vaupotič s.p.</strong>
                        </p>
                        <p>
                            <span class="font-medium">E-pošta:</span> info@kulfood.si
                        </p>
                        <p>
                            <span class="font-medium">Država:</span> Republika Slovenija
                        </p>
                        <p class="text-sm text-gray-600 italic mt-4">
                            Ajdovščina 4, 1000 Ljubljana
                        </p>
                    </div>
                </div>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">1. Uvodne določbe</h2>
                    <p class="text-gray-700 mb-4 leading-relaxed">
                        Ta Politika zasebnosti določa, katere osebne podatke zbira ponudnik KulFood – Rok Vaupotič s.p. (v nadaljevanju: Ponudnik), kako jih obdeluje, za katere namene, koliko časa jih hrani ter katere pravice imajo posamezniki v zvezi z obdelavo osebnih podatkov.
                    </p>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Ponudnik spoštuje veljavno zakonodajo, vključno z:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6 mb-4">
                        <li class="leading-relaxed">Splošno uredbo EU o varstvu podatkov (GDPR),</li>
                        <li class="leading-relaxed">Zakonom o varstvu osebnih podatkov (ZVOP-2),</li>
                        <li class="leading-relaxed">Zakonom o elektronskih komunikacijah (ZEKom-2),</li>
                        <li class="leading-relaxed">sektorskimi predpisi, ki urejajo varnost živil in spletno poslovanje.</li>
                    </ul>
                    <p class="text-gray-700 leading-relaxed bg-theme-green-light p-4 rounded-lg border-l-4 border-theme-green-dark">
                        Z uporabo spletne strani, registracijskega obrazca ali oddajo naročila se uporabnik seznani s to Politiko zasebnosti in jo sprejme.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">2. Upravljavec osebnih podatkov</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Upravljavec osebnih podatkov je:
                    </p>
                    <div class="space-y-2 text-gray-700 ml-4">
                        <p>
                            <strong class="text-gray-900">KulFood – Rok Vaupotič s.p.</strong>
                        </p>
                        <p>
                            <span class="font-medium">E-pošta:</span> info@kulfood.si
                        </p>
                        <p class="text-sm text-gray-600 italic">
                            Ajdovščina 4, 1000 Ljubljana
                        </p>
                    </div>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">3. Katere osebne podatke zbiramo?</h2>
                    <p class="text-gray-700 mb-6 leading-relaxed">
                        Ponudnik zbira samo tiste podatke, ki so nujni za delovanje storitve, in sicer:
                    </p>
                    
                    <div class="space-y-6">
                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">3.1. Podatki ob registraciji in uporabi spletne strani</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">ime in priimek,</li>
                                <li class="leading-relaxed">e-poštni naslov,</li>
                                <li class="leading-relaxed">telefonska številka,</li>
                                <li class="leading-relaxed">geslo (šifrirano),</li>
                                <li class="leading-relaxed">naziv podjetja, v katerem je uporabnik zaposlen (če je relevantno).</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">3.2. Podatki o naročilih</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">izbrani obroki,</li>
                                <li class="leading-relaxed">naslov dostave (delovno mesto ali dogovorjena lokacija),</li>
                                <li class="leading-relaxed">čas naročila,</li>
                                <li class="leading-relaxed">način plačila,</li>
                                <li class="leading-relaxed">znesek transakcije.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">3.3. Podatki za izvedbo plačil (Stripe)</h3>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Ponudnik NE hrani podatkov o bančnih karticah.
                            </p>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Obdelavo izvede Stripe, ki je samostojen upravljavec skladno z GDPR.
                            </p>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Stripe lahko zbira:
                            </p>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">zadnje štiri številke kartice,</li>
                                <li class="leading-relaxed">stanje transakcije,</li>
                                <li class="leading-relaxed">ID transakcije,</li>
                                <li class="leading-relaxed">podrobnosti o uspešnosti plačila.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">3.4. Komunikacija in dokumentacija</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">podatki o reklamacijah,</li>
                                <li class="leading-relaxed">odpovedih naročil,</li>
                                <li class="leading-relaxed">e-poštna komunikacija.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">3.5. Piškotki in analitični podatki</h3>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Spletna stran uporablja:
                            </p>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">nujne piškotke,</li>
                                <li class="leading-relaxed">funkcionalne piškotke,</li>
                                <li class="leading-relaxed">Google Analytics (anonimiziran IP naslov),</li>
                                <li class="leading-relaxed">tehnične dnevnike o delovanju strani.</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">4. Nameni in pravne podlage obdelave</h2>
                    
                    <div class="space-y-6">
                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">4.1. Izvajanje pogodbe (6(1)(b) GDPR)</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">registracija uporabnika,</li>
                                <li class="leading-relaxed">obdelava naročil,</li>
                                <li class="leading-relaxed">priprava in dostava obrokov,</li>
                                <li class="leading-relaxed">komunikacija v postopku reklamacij ali odpovedi,</li>
                                <li class="leading-relaxed">zaračunavanje in evidenca transakcij.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">4.2. Izpolnjevanje zakonskih obveznosti (6(1)(c) GDPR)</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">vodenje računovodskih evidenc po ZDDV-1 in ZDavP-2,</li>
                                <li class="leading-relaxed">arhiviranje določenih evidenc v skladu s slovensko zakonodajo.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">4.3. Zakoniti interes ponudnika (6(1)(f) GDPR)</h3>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">preprečevanje zlorab,</li>
                                <li class="leading-relaxed">zagotavljanje varnosti informacijskega sistema,</li>
                                <li class="leading-relaxed">analitika uporabe spletne strani za optimizacijo (Google Analytics),</li>
                                <li class="leading-relaxed">dokazovanje pravilnosti poslovanja v primeru sporov.</li>
                            </ul>
                        </div>

                        <div>
                            <h3 class="text-xl font-semibold text-gray-900 mb-3">4.4. Privolitev uporabnika (6(1)(a) GDPR)</h3>
                            <p class="text-gray-700 mb-3 leading-relaxed">
                                Kadar zakon zahteva privolitev, jo uporabnik poda samostojno, izrecno in nedvoumno, npr. za:
                            </p>
                            <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                                <li class="leading-relaxed">posamezne vrste piškotkov,</li>
                                <li class="leading-relaxed">prejemanje novic ali promocijskih sporočil (če bodo uvedena).</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">5. Hramba podatkov</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Podatki se hranijo:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                        <li class="leading-relaxed">podatki o registraciji: dokler ima uporabnik aktivni račun,</li>
                        <li class="leading-relaxed">podatki o naročilih in računih: 10 let (zakonska obveznost),</li>
                        <li class="leading-relaxed">podatki o reklamacijah: najmanj 1 leto po zaključku postopka,</li>
                        <li class="leading-relaxed">analitični podatki: do 2 leti, skladno z nastavitvami Google Analytics,</li>
                        <li class="leading-relaxed">podatki, za katere je bila podana privolitev: do preklica privolitve.</li>
                    </ul>
                    <p class="text-gray-700 mt-4 leading-relaxed">
                        Po izteku rokov so podatki anonimizirani ali varno izbrisani.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">6. Kdo ima dostop do podatkov? (uporabniki podatkov)</h2>
                    <p class="text-gray-700 mb-4 leading-relaxed">
                        Podatki se NE posredujejo tretjim osebam, razen kadar je to nujno za izvajanje storitve ali zakonsko zahtevano.
                    </p>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Dostop imajo:
                    </p>
                    <ol class="list-decimal list-inside space-y-3 text-gray-700 ml-4">
                        <li class="pl-2 leading-relaxed">Ponudnik in njegovi zaposleni (v primeru širitve poslovanja) – samo tisti, ki podatke nujno potrebujejo.</li>
                        <li class="pl-2 leading-relaxed">Stripe (izvajanje plačil).</li>
                        <li class="pl-2 leading-relaxed">Zunanje IT podjetje / gostovanje – izključno za tehnično podporo in varnost sistema.</li>
                        <li class="pl-2 leading-relaxed">Računovodski servis – za obveznosti po Zakonu o davku na dodano vrednost.</li>
                        <li class="pl-2 leading-relaxed">Državni organi, kadar to zahteva zakon.</li>
                    </ol>
                    <p class="text-gray-700 mt-4 leading-relaxed">
                        Z vsemi obdelovalci so sklenjene pogodbe o obdelavi osebnih podatkov po 28. členu GDPR.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">7. Piškotki (Cookies)</h2>
                    <p class="text-gray-700 mb-4 leading-relaxed">
                        Spletna stran uporablja piškotke, kar je urejeno v ločeni Politiki piškotkov.
                    </p>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Vsebovani so:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6 mb-4">
                        <li class="leading-relaxed">nujni piškotki (brez privolitve),</li>
                        <li class="leading-relaxed">funkcionalni piškotki (za izboljšanje uporabniške izkušnje),</li>
                        <li class="leading-relaxed">Google Analytics – anonimizacija IP je omogočena (kar pomeni, da Google ne prejme celotnega IP naslova).</li>
                    </ul>
                    <p class="text-gray-700 leading-relaxed">
                        Uporabnik lahko piškotke kadarkoli izbriše ali upravlja preko nastavitev brskalnika ali preko pasice o piškotkih.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">8. Varovanje podatkov</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Ponudnik uporablja tehnične in organizacijske ukrepe, kot so:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6 mb-4">
                        <li class="leading-relaxed">TLS/SSL šifriranje,</li>
                        <li class="leading-relaxed">omejen dostop do podatkov,</li>
                        <li class="leading-relaxed">redne varnostne posodobitve,</li>
                        <li class="leading-relaxed">varne geselske politike,</li>
                        <li class="leading-relaxed">ločene baze podatkov,</li>
                        <li class="leading-relaxed">nadzor dostopnih pravic.</li>
                    </ul>
                    <p class="text-gray-700 leading-relaxed">
                        V primeru kršitve varnosti podatkov bo Ponudnik, kadar je to potrebno, o tem obvestil Informacijskega pooblaščenca in prizadete posameznike skladno s 33. in 34. členom GDPR.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">9. Pravice posameznikov</h2>
                    <p class="text-gray-700 mb-4 leading-relaxed">
                        Uporabnik ima pravico, da kadarkoli:
                    </p>
                    <ol class="list-decimal list-inside space-y-3 text-gray-700 ml-4 mb-4">
                        <li class="pl-2 leading-relaxed">zahteva dostop do svojih osebnih podatkov (15. člen GDPR),</li>
                        <li class="pl-2 leading-relaxed">zahteva popravek netočnih ali nepopolnih podatkov (16. člen GDPR),</li>
                        <li class="pl-2 leading-relaxed">zahteva izbris ("pravica do pozabe") – kadar so izpolnjeni pogoji iz 17. člena GDPR,</li>
                        <li class="pl-2 leading-relaxed">zahteva omejitev obdelave (18. člen GDPR),</li>
                        <li class="pl-2 leading-relaxed">ugovarja obdelavi – kadar je podlaga zakoniti interes (21. člen GDPR),</li>
                        <li class="pl-2 leading-relaxed">zahteva prenos podatkov k drugemu upravljavcu (20. člen GDPR),</li>
                        <li class="pl-2 leading-relaxed">prekliče privolitev – kadar je obdelava temeljila na privolitvi.</li>
                    </ol>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Vse zahteve lahko uporabnik pošlje na info@kulfood.si.
                    </p>
                    <p class="text-gray-700 leading-relaxed">
                        Ponudnik bo nanje odgovoril v roku 30 dni, skladno z GDPR.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">10. Prenos podatkov v tretje države</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Podatki se praviloma ne prenašajo v tretje države.
                    </p>
                    <p class="text-gray-700 leading-relaxed">
                        Stripe lahko obdeluje podatke tudi izven EU, vendar izključno v državah in pri partnerjih, ki zagotavljajo ustrezno raven varstva podatkov v skladu s 45. in 46. členom GDPR (npr. standardne pogodbene klavzule).
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">11. Avtomatizirano sprejemanje odločitev</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Ponudnik ne uporablja:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6">
                        <li class="leading-relaxed">profiliranja,</li>
                        <li class="leading-relaxed">avtomatiziranega sprejemanja odločitev,</li>
                        <li class="leading-relaxed">sistemov za avtomatsko segmentiranje uporabnikov.</li>
                    </ul>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">12. Spremembe Politike zasebnosti</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Ponudnik si pridržuje pravico do sprememb Politike zasebnosti zaradi:
                    </p>
                    <ul class="list-disc list-inside space-y-2 text-gray-700 ml-6 mb-4">
                        <li class="leading-relaxed">izboljšanja storitev,</li>
                        <li class="leading-relaxed">sprememb zakonodaje,</li>
                        <li class="leading-relaxed">tehnoloških sprememb.</li>
                    </ul>
                    <p class="text-gray-700 leading-relaxed">
                        Vse spremembe bodo objavljene na spletni strani, datum zadnje spremembe pa bo ustrezno ažuriran.
                    </p>
                </section>

                <section class="mb-10">
                    <h2 class="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-gray-200">13. Kontakt za varstvo osebnih podatkov</h2>
                    <p class="text-gray-700 mb-3 leading-relaxed">
                        Za vprašanja ali uveljavljanje pravic se uporabnik lahko obrne na:
                    </p>
                    <div class="space-y-2 text-gray-700 ml-4">
                        <p>
                            <strong class="text-gray-900">KulFood – Rok Vaupotič s.p.</strong>
                        </p>
                        <p>
                            <span class="font-medium">E-pošta:</span> info@kulfood.si
                        </p>
                    </div>
                </section>

                <div class="mt-12 pt-6 border-t-2 border-gray-200">
                    <p class="text-sm text-gray-500 text-center">
                        Datum zadnje posodobitve: 30. 12. 2025
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
